import { useMemo } from 'react';
import {
  debounceFn,
  loadCouponPreviewV2Request,
  selectCouponAmountErrorTypeV2,
  selectCouponLimitsV2,
  selectCurrentPaymentSourceV2,
  selectUserCurrency,
  useLocalizedNumberFormatters,
} from '@mwl/core-lib';

import { useTypedDispatch, useTypedSelector } from '@/hooks';

import type { CouponAmountErrors } from './CouponSum.types';

const amountErrors: CouponAmountErrors = {
  UNDER_LIMIT: (value, currency) => [
    'coupon.error.min_amount',
    {
      defaultValue: 'Minimum bet amount: {{ value }} {{ currency }}',
      replace: { value: value || 0, currency: currency ?? '' },
    },
  ],
  ABOVE_LIMIT: (value, currency) => [
    'coupon.error.max_amount',
    {
      defaultValue: 'Maximum bet amount: {{ value }} {{ currency }}',
      replace: { value: value || 0, currency: currency ?? '' },
    },
  ],
  NOT_ENOUGH_BALANCE: (value, currency) => [
    'coupon.error.not_enough_amount',
    {
      defaultValue: 'Insufficient funds to place a bet. Minimum bet amount: {{ value }} {{ currency }}',
      replace: { value: value || 0, currency: currency ?? '' },
    },
  ],
};

export const useAmountError = () => {
  const limits = useTypedSelector(selectCouponLimitsV2);
  const amountErrorType = useTypedSelector(selectCouponAmountErrorTypeV2);
  const currentPaymentSource = useTypedSelector(selectCurrentPaymentSourceV2);
  const currency = useTypedSelector(selectUserCurrency);

  const { formatPriceByCurrencyCodeWithLocale } = useLocalizedNumberFormatters();

  return useMemo(() => {
    const errorFn = amountErrorType ? amountErrors[amountErrorType] : null;

    if (!errorFn) {
      return null;
    }

    if (amountErrorType === 'UNDER_LIMIT') {
      const minLimit = limits?.min?.amount || 0;
      return errorFn(formatPriceByCurrencyCodeWithLocale(Number(minLimit), currency));
    }

    if (amountErrorType === 'ABOVE_LIMIT') {
      const maxLimit =
        limits?.max?.amount && currentPaymentSource.type === 'BALANCE'
          ? Math.min(currentPaymentSource.amount, Number(limits.max.amount))
          : currentPaymentSource.amount;

      return errorFn(formatPriceByCurrencyCodeWithLocale(maxLimit, currency));
    }

    if (amountErrorType === 'NOT_ENOUGH_BALANCE') {
      const minLimit = limits?.min?.amount || 0;
      return errorFn(formatPriceByCurrencyCodeWithLocale(Number(minLimit), currency));
    }

    return errorFn();
  }, [amountErrorType, currentPaymentSource, limits, formatPriceByCurrencyCodeWithLocale, currency]);
};

export const useDebouncedLoadPreview = (requestDebounceTime = 300) => {
  const dispatch = useTypedDispatch();

  return useMemo(
    () => debounceFn(() => dispatch(loadCouponPreviewV2Request()), requestDebounceTime),
    [dispatch, requestDebounceTime],
  );
};
